// Poziomy CEFR, etapy i format lekcji v2 (SPEC §21). Tylko typy i stałe — bez wejścia/wyjścia, więc działa w Node, na serwerze i w przeglądarce.

import type { Cast } from './characters.ts';

export const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type Level = (typeof LEVELS)[number];

/** Etapy = podpoziomy CEFR-J (A1.1 … B2.2); C1 i C2 są jednoetapowe. Kolejność = kolejność nauki. */
export const STAGES = ['A1.1', 'A1.2', 'A1.3', 'A2.1', 'A2.2', 'B1.1', 'B1.2', 'B2.1', 'B2.2', 'C1', 'C2'] as const;
export type Stage = (typeof STAGES)[number];

/** Poziomy objęte kursem w tej wersji (C1–C2 później — SPEC §21.2). */
export const COURSE_LEVELS: readonly Level[] = ['A1', 'A2', 'B1', 'B2'];

/** Jedna paczka = tyle nowych słów-celów w lekcji (SPEC §21.2: lekcje = ⌈słowa treści ÷ 5⌉). */
export const PACK_SIZE = 5;
/** Najmniej celów w lekcji: pełna lekcja ma PACK_SIZE, a resztę słów poziomu dzielimy tak, by żadna lekcja nie miała mniej niż tyle (SPEC §23.7). */
export const PACK_MIN = 3;
/** Po każdych tylu lekcjach z nowymi słowami jest lekcja kontrolna (bez nowych słów). */
export const CHECKPOINT_EVERY = 5;

export function isLevel(v: unknown): v is Level {
  return typeof v === 'string' && (LEVELS as readonly string[]).includes(v);
}
export function isStage(v: unknown): v is Stage {
  return typeof v === 'string' && (STAGES as readonly string[]).includes(v);
}
export const levelRank = (l: Level): number => LEVELS.indexOf(l);
export const stageRank = (s: Stage): number => STAGES.indexOf(s);
export function levelOfStage(s: Stage): Level {
  return s.slice(0, 2) as Level;
}

/** Ile lekcji z nowymi słowami potrzeba na tyle słów treści. */
export function lessonCount(contentWords: number, pack: number = PACK_SIZE): number {
  return Math.ceil(contentWords / pack);
}
/**
 * Ile celów ma każda lekcja poziomu o tylu słowach treści: pełne lekcje po `pack`, a gdy zostaje reszta mniejsza niż `min`,
 * ostatnie słowa dzielimy na dwie krótsze lekcje (np. A2: 241 = 47×5 + 3 + 3). Liczba lekcji = ⌈słowa ÷ pack⌉.
 */
export function lessonSizes(contentWords: number, pack: number = PACK_SIZE, min: number = PACK_MIN): number[] {
  if (contentWords <= 0) return [];
  const full = Math.floor(contentWords / pack);
  const rest = contentWords - full * pack;
  if (rest === 0) return Array<number>(full).fill(pack);
  if (rest >= min || full === 0) return [...Array<number>(full).fill(pack), rest];
  const total = pack + rest;
  const first = Math.ceil(total / 2);
  return [...Array<number>(full - 1).fill(pack), first, total - first];
}
/**
 * Numer lekcji w kursie (od 1) — jej miejsce na liście kursu, tak jak widzi je dziecko (SPEC §23.24). Pole `seq` numeruje lekcje w obrębie poziomu
 * (A2, B1 i B2 zaczynają od 1), więc nie nadaje się do napisów w aplikacji. Lekcji spoza listy zostaje jej `seq`.
 */
export function lessonNo(course: ReadonlyArray<{ id: string }>, lesson: { id: string; seq: number }): number {
  const at = course.findIndex((l) => l.id === lesson.id);
  return at < 0 ? lesson.seq : at + 1;
}
/** Ile lekcji kontrolnych dochodzi do tylu lekcji z nowymi słowami. */
export function checkpointCount(lessons: number, every: number = CHECKPOINT_EVERY): number {
  return Math.ceil(lessons / every);
}

/** Limity długości (w słowach) na etap: zdania opisowe i kwestie scenki, a osobno wzbogacone zdania kroku 2. */
export interface StageLimits {
  min: number;
  max: number;
  enrichMax: number;
  /** Średnia długość KWESTII/zdania (kroki 1 i 3) poniżej tej wartości = ostrzeżenie: lekcja jest za prosta jak na etap. */
  avgMin: number;
  /** Średnia długość zdań „z treścią” (bez okrzyków typu Wow!/Yes./Of course!) poniżej tej wartości = ostrzeżenie: zdania są telegraficzne. */
  avgSentenceMin: number;
}
/**
 * Zdania rosną z etapem (SPEC §21.4a, skalibrowane w §22.12): korpus CEFR-SP (zdania z podręczników i lektur oznaczone przez
 * ekspertów) daje średnie A1 7,7 · A2 10,9 · B1 15,2 · B2 18,0 · C1 19,0 · C2 19,2 słowa — to zdania PISANE; w mówionym dialogu
 * celujemy w ok. 55–70% tej długości (założenie, nie pomiar). `max` to górna granica pojedynczego zdania, `avgMin` i `avgSentenceMin`
 * pilnują, żeby poziom naprawdę rósł.
 */
export const STAGE_LIMITS: Record<Stage, StageLimits> = {
  'A1.1': { min: 3, max: 8, enrichMax: 11, avgMin: 5.5, avgSentenceMin: 4.2 },
  'A1.2': { min: 3, max: 9, enrichMax: 12, avgMin: 6, avgSentenceMin: 4.6 },
  'A1.3': { min: 4, max: 10, enrichMax: 13, avgMin: 6.5, avgSentenceMin: 5 },
  'A2.1': { min: 4, max: 12, enrichMax: 15, avgMin: 8, avgSentenceMin: 5.8 },
  'A2.2': { min: 5, max: 13, enrichMax: 16, avgMin: 9, avgSentenceMin: 6.5 },
  'B1.1': { min: 5, max: 16, enrichMax: 19, avgMin: 10.5, avgSentenceMin: 8 },
  'B1.2': { min: 6, max: 17, enrichMax: 20, avgMin: 12, avgSentenceMin: 9 },
  'B2.1': { min: 6, max: 19, enrichMax: 22, avgMin: 13.5, avgSentenceMin: 10 },
  'B2.2': { min: 7, max: 21, enrichMax: 24, avgMin: 14.5, avgSentenceMin: 11 },
  C1: { min: 7, max: 23, enrichMax: 26, avgMin: 16, avgSentenceMin: 12.5 },
  C2: { min: 8, max: 26, enrichMax: 29, avgMin: 17, avgSentenceMin: 13.5 },
};

/** Kwestia dialogu może mieć dwa krótkie zdania: limit etapu liczy się na KAŻDE zdanie, a cała kwestia ma limit + tyle słów (SPEC §22.10). */
export const DIALOGUE_LINE_EXTRA = 4;

/** Długość dialogu (liczba kwestii) i liczba kwestii dziecka rosną z poziomem: A — 6–8 (3–4), B — 8–10 (4–5), C — 10–12 (5–6). */
export interface DialogueShape {
  min: number;
  max: number;
  learnerMin: number;
  learnerMax: number;
}
export function dialogueShape(stage: Stage): DialogueShape {
  const l = levelOfStage(stage);
  if (l === 'A1' || l === 'A2') return { min: 6, max: 8, learnerMin: 3, learnerMax: 4 };
  if (l === 'B1' || l === 'B2') return { min: 8, max: 10, learnerMin: 4, learnerMax: 5 };
  return { min: 10, max: 12, learnerMin: 5, learnerMax: 6 };
}

/** Reguły kształtu lekcji (lint pilnuje ich w `lessonLint.ts`). */
export const LESSON_SHAPE = {
  describeLines: 2,
  enrichLines: 2,
  /** Słowa dodane tylko w kroku 2 i objaśnione po polsku (nie są celami). */
  maxExtras: 5,
  /** Ile słów wolno dopisać do jednego zdania w kroku 2. */
  enrichMinInsert: 1,
  enrichMaxInsert: 4,
  /** Pytania kroku 4 (SPEC §23.26): dokładnie jedno na cel; każde ma cztery odpowiedzi — cel i tyle błędnych. */
  quizDistractors: 3,
  /** „Zmień zdanie” (SPEC §23.13): tyle dodatkowych zdań z danym słowem na cel (razem z zdaniami lekcji dziecko widzi na karcie 3–4 różne konteksty). */
  altsPerTarget: 2,
  /** Karty „Co powiesz?” w lekcji (opcjonalne): 1–2, każda ćwiczy jeden cel (SPEC §23.13). */
  sayMax: 2,
} as const;

/** Błędnych opcji na karcie „Co powiesz?”: A1–A2 dwie (razem 3 karty jak na makiecie), od B1 trzy. */
export function sayDistractors(stage: Stage): number {
  const l = levelOfStage(stage);
  return l === 'A1' || l === 'A2' ? 2 : 3;
}

// ---------------------------------------------------------------------------------------------
// Format lekcji v2 (SPEC §21.4, uzupełniony 2026-10-16 o dialog z podpisanymi rozmówcami)
// ---------------------------------------------------------------------------------------------

/**
 * Kotwica pamięciowa słowa (SPEC §23.24, od patch-45 §23.26 stoi w polu obrazka pytania kroku 4): dziecko ma zobaczyć SŁOWO, a nie scenę, w której ono pada.
 * `draw` — opis do generatora (po angielsku, tylko to, co ma być widać: jeden obiekt, osoba albo czynność, bez postaci z biblii i bez przeczeń);
 * `situationPl` — słowa nie da się narysować jednym obrazkiem („hello”, „please”, „not”, „chociaż”): pytanie pokazuje kafelek z emoji celu i tym jednym zdaniem po polsku.
 */
export type TargetAnchor = { draw: string; situationPl?: never } | { situationPl: string; draw?: never };

export interface LessonTarget {
  /** Hasło z listy CEFR-J poziomu lekcji (mała litera; może być wielowyrazowe, np. „ice cream”). */
  word: string;
  pl: string;
  /** Emoji słowa: zapas, gdy obrazek jeszcze nie powstał, i duża ikona kafelka, gdy słowa nie da się narysować (`anchor.situationPl`). */
  emoji?: string;
  /** Kotwica pamięciowa: obrazek tego jednego słowa (ludzik albo jedna rzecz na białym tle) albo — gdy się go nie da narysować — kafelek z sytuacją; stoi przy pytaniu kroku 4 (SPEC §23.24, §23.26). */
  anchor?: TargetAnchor;
  /**
   * Dodatkowe zdania z tym słowem do przycisku „Zmień zdanie” (dziś nieużywanego — kart słów nie ma od patch-45, SPEC §23.26): dokładnie `LESSON_SHAPE.altsPerTarget`, każde z tłumaczeniem i nagraniem,
   * pisane z góry (nie w aplikacji) i sprawdzane linterem jak reszta lekcji (SPEC §23.13). Pole opcjonalne — lekcje bez banku pokazują tylko zdania lekcji.
   */
  alts?: LessonLine[];
}

export interface LessonLine {
  text: string;
  pl: string;
}

/** Zdanie kroku 2: `base` = indeks zdania z kroku 1, z którego powstało przez dopisanie przymiotników/przysłówków. */
export interface EnrichLine extends LessonLine {
  base: number;
}

/** Słowo dopisane w kroku 2 (nie cel): przymiotnik lub przysłówek z poziomu lekcji lub niższego, z tłumaczeniem. */
export interface LessonExtra {
  word: string;
  pl: string;
}

/** Jedna kwestia dialogu w scence; `who` to imię mówiącego (z listy dozwolonych imion). */
export interface DialogueLine extends LessonLine {
  who: string;
}

/**
 * Pytanie kroku 4 „Popatrz i wybierz” (SPEC §23.26): w polu obrazka stoi kotwica CELU (jego własny obrazek albo kafelek), pod nią pytanie i cztery słowa do wyboru —
 * cel i trzy błędne. Każdy cel ma dokładnie jedno pytanie; dobra odpowiedź to TEN cel (może mieć rodzajnik, np. „an apple”), a pytanie go nie zdradza. Po odpowiedzi uczeń
 * widzi słowo, polskie znaczenie i słyszy je. Kolejność odpowiedzi losuje sesja.
 */
export interface QuizItem {
  /** Cel lekcji, o który pyta pytanie. */
  target: string;
  /** Pytanie po angielsku: *What is this?* · *Who is this?* · *What is he doing?* przy obrazku, *Which word fits?* przy kafelku. Te same pytania mają jedno wspólne nagranie. */
  q: string;
  pl: string;
  answer: string;
  /** Trzy błędne odpowiedzi: nie mogą być widoczne na obrazku celu, jego synonimem ani nazwą nadrzędną (dwie dobre odpowiedzi = zepsute pytanie). */
  distractors: string[];
}

/** Błędna opcja karty „Co powiesz?”: prawdziwe, poprawne zdanie z tej samej sytuacji, tylko nie pasujące do roli dziecka. */
export interface SayDistractor extends LessonLine {
  /** Kto to zdanie mówi w tej sytuacji (Tom / Emma) — wybiera głos nagrania. */
  who: string;
  /** Jedna linijka po polsku: dlaczego to zdanie tu nie pasuje („Tak pyta sprzedawca, nie klient.”). */
  whyPl: string;
}

/**
 * Karta „Co powiesz?” (SPEC §23.13, makieta z 17.10): sytuacja po polsku, pod spodem 3 zdania z 🔊 (bez obrazka od patch-45, SPEC §23.26); dziecko MÓWI to, które pasuje
 * (mikrofon), a nie klika. Odpowiedź i jej warianty ocenia `say.ts` (tekst rozpoznany przez Azure), wymowę — Azure.
 */
export interface SayItem {
  /** Cel lekcji ćwiczony przez kartę — musi występować w poprawnej odpowiedzi. */
  target: string;
  /**
   * Polecenie po polsku (SPEC §23.36): co dziecko ma zrobić — pogrubione w brzoskwiniowym polu nad odpowiedziami, zawsze widoczne („Zapytaj Toma, gdzie jest Ben.”, „Jesteś w sklepie. Poproś o banana.”).
   * Nie zawiera pytania „Co mówisz?” (zastępuje je podtytuł pola: „Wybierz najlepsze pytanie po angielsku.” albo „Powiedz po angielsku.”) i nie zdradza angielskiej odpowiedzi.
   * Pole zachowało nazwę z czasów, gdy był tu opis sytuacji; formy zależne od płci jak w innych tekstach po polsku: `głodn{y|a}`.
   */
  situationPl: string;
  /**
   * Kwestia rozmówcy (druga postać dialogu — nie ta, która mówi odpowiedź): angielskie zdanie w dymku nad kartami, z imieniem i przyciskami 🔊 / 🐢 (makieta „Co powiesz?”, SPEC §23.35.6).
   * Wprowadza sytuację, ale jej nie rozstrzyga; do dwóch zdań.
   */
  partner: string;
  /** Dosłowne tłumaczenie kwestii rozmówcy na polski: tłumaczenie 文A pod tekstem dymka (SPEC §23.36). Formy zależne od płci: `{y|a}`. */
  partnerPl: string;
  /** Poprawne zdanie do powiedzenia (jedno zdanie; mówi je dziecko jako druga postać). */
  answer: LessonLine;
  /** Inne poprawne wypowiedzi, które też zaliczamy („A banana, please.”) — tylko tekst, bez nagrań. */
  alsoOk?: string[];
  distractors: SayDistractor[];
}

export interface GrammarSpotExample {
  text: string;
  focus: string[];
}

export interface LessonScene {
  /** Kto jest na obrazkach lekcji: dorośli (domyślnie) albo dzieci — wpływa na wygląd Toma, Emmy i reszty w obrazku kroków 1–2 (SPEC §22.11). Scenka nie ma własnego obrazka (od patch-45, SPEC §23.26). */
  cast?: Cast;
  /** Miejsce scenki (warzywniak, szkoła…), po angielsku i po polsku — nagłówek nad dialogiem. */
  setting: string;
  settingPl: string;
  /** Rola dziecka: jego kwestie dziecko mówi, kwestie drugiej osoby czyta jej głos (każda postać ma stały głos — src/lib/characters.ts). */
  learner: string;
  /** Ustawiane tylko w czasie sesji przez `withRole`, gdy rola dziecka różni się od autorskiej (chłopiec gra Toma zamiast Emmy): rola z treści. Krok „Co powiesz?” jest pisany dla niej (dziecko to „ty” z opisu sytuacji,
   *  rozmówcą jest druga postać), więc zostaje taki sam dla obu płci (SPEC §23.29.6). Nie występuje w plikach treści. */
  authoredLearner?: string;
  dialogue: DialogueLine[];
}

export interface LessonV2 {
  id: string;
  level: Level;
  stage: Stage;
  unit: string;
  seq: number;
  theme: string;
  themePl: string;
  /**
   * Szkielet gramatyczny lekcji (src/lib/grammar.ts) i dwa przykłady do karty „Zwróć uwagę” (SPEC §22.2): `text` to dokładnie zdanie
   * z kroku 1, 2 lub scenki, `focus` — słowa do pogrubienia (np. ["eats", "drinks"]).
   */
  grammar: { id: string; label: string; spot?: { examples: GrammarSpotExample[] } };
  targets: LessonTarget[];
  /** Temat słów lekcji: „kategoria/podkategoria” z data/topics/taxonomy.json (nagłówek lekcji i mapa postępu). */
  topic: string;
  /**
   * Krok 1: dwa zdania tworzące mini-opowiadanie o obrazku (drugie wynika z pierwszego). Obrazek rysujemy ze zdań KROKU 2
   * (wzbogaconych) — dlatego `imagePrompt` opisuje ujęcie i wymienia dopisane przymiotniki; ten sam obrazek służy krokom 1 i 2.
   */
  describe: { image: string; imagePrompt: string; lines: LessonLine[] };
  enrich: { lines: EnrichLine[]; extras: LessonExtra[] };
  scene: LessonScene;
  /** Pytania kroku 4 „Popatrz i wybierz”: po jednym na każdy cel. */
  quiz: QuizItem[];
  /** Karty „Co powiesz?” (krok 6, na zmianę z układaniem zdania); opcjonalne — pilotaż w a1-001..003. */
  say?: SayItem[];
}

export interface LessonsFile {
  version: 2;
  _comment?: string;
  lessons: LessonV2[];
}
